import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiPackage,
  FiArrowLeft,
  FiAlertCircle,
  FiCheckCircle,
  FiCalendar,
  FiClock,
  FiHome,
  FiPhone,
  FiUser,
  FiMapPin,
  FiRefreshCw,
} from "react-icons/fi";
import { BsTruck, BsCreditCard2Back, BsBox } from "react-icons/bs";
import { IoReceiptOutline } from "react-icons/io5";
import { MdOutlineLoop } from "react-icons/md";
import { toast } from "react-toastify";
import OrderDetailsSkeleton from "./OrderDetailsSkeleton";

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExchangeModal, setShowExchangeModal] = useState(false);
  const [exchangeLoading, setExchangeLoading] = useState(false);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/orders/get-order-by-id/${orderId}`
        );

        if (response.data) {
          setOrder(response.data);
        } else {
          setError("Order not found");
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Failed to fetch order details. Please try again later.");
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrderDetails();
    }
  }, [orderId]);

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleExchangeRequest = () => {
    setShowExchangeModal(true);
  };

  const confirmExchange = async () => {
    try {
      setExchangeLoading(true);
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/orders/order-exchange-request/${orderId}`
      );

      if (response.data) {
        setOrder({
          ...order,
          exchangeRequest: {
            isRequested: true,
            status: "Requested",
            requestedAt: new Date().toISOString(),
          },
        });

        toast.success("Exchange request submitted successfully!");
      }
    } catch (err) {
      console.error("Error requesting exchange:", err);
      toast.error("Failed to submit exchange request. Please try again.");
    } finally {
      setExchangeLoading(false);
      setShowExchangeModal(false);
    }
  };

  // Status badge component with appropriate colors
  const StatusBadge = ({ status }) => {
    let bgColor = "bg-gray-200";
    let icon = null;

    switch (status) {
      case "Delivered":
        bgColor = "bg-green-500 text-white";
        icon = <FiCheckCircle className="mr-1" />;
        break;
      case "Shipped":
        bgColor = "bg-gray-500 text-white";
        icon = <BsTruck className="mr-1" />;
        break;
      case "Pending":
        bgColor = "bg-yellow-500 text-white";
        icon = <FiClock className="mr-1" />;
        break;
      case "Cancelled":
        bgColor = "bg-red-500 text-white";
        icon = <FiAlertCircle className="mr-1" />;
        break;
      default:
        bgColor = "bg-gray-500 text-white";
        icon = <FiPackage className="mr-1" />;
    }

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${bgColor} flex items-center shadow-sm`}
      >
        {icon}
        {status}
      </span>
    );
  };

  const ExchangeBadge = ({ exchangeRequest }) => {
    if (!exchangeRequest || !exchangeRequest.isRequested) return null;

    let bgColor = "bg-gray-500 text-white";
    let icon = <MdOutlineLoop className="mr-1" />;

    switch (exchangeRequest.status) {
      case "Completed":
        bgColor = "bg-green-500 text-white";
        icon = <FiCheckCircle className="mr-1" />;
        break;
      case "Requested":
        bgColor = "bg-gray-500 text-white";
        icon = <FiClock className="mr-1" />;
        break;
      case "Rejected":
        bgColor = "bg-red-500 text-white";
        icon = <FiAlertCircle className="mr-1" />;
        break;
      case "Approved":
        bgColor = "bg-yellow-500 text-white";
        icon = <FiAlertCircle className="mr-1" />;
        break;
      default:
        bgColor = "bg-gray-500 text-white";
    }

    return (
      <span
        className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${bgColor} flex items-center shadow-sm`}
      >
        {icon}
        Exchange: {exchangeRequest.status}
      </span>
    );
  };

  // Calculate order summary values based on API response structure
  const calculateSubTotal = () =>
    (order?.products || []).reduce(
      (total, item) => total + item.price * item.quantity,
      0
    );

  const calculateGstTax = () =>
    (order?.products || []).reduce(
      (total, item) => total + item.gst * item.quantity,
      0
    );

  const calculateExtraTax = () =>
    (order?.products || []).reduce(
      (total, item) => total + item.extraTax * item.quantity,
      0
    );

  const calculateShippingCharge = () =>
    (order?.products || []).reduce(
      (total, item) => total + item.shippingCharge * item.quantity,
      0
    );

  const subTotal = calculateSubTotal();
  const gstTax = calculateGstTax();
  const extraTax = calculateExtraTax();
  const shippingCharge = calculateShippingCharge();
  const totalPrice = subTotal + gstTax + extraTax + shippingCharge;

  // Exchange Modal Component
  const ExchangeModal = () => (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${
        showExchangeModal ? "visible" : "hidden"
      }`}
    >
      <div
        className="fixed inset-0 bg-black opacity-50"
        onClick={() => !exchangeLoading && setShowExchangeModal(false)}
      ></div>
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-auto z-10 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Exchange Request
        </h3>
        <p className="text-gray-600 mb-6">
          Do you want to exchange this order?
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowExchangeModal(false)}
            disabled={exchangeLoading}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={confirmExchange}
            disabled={exchangeLoading}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md flex items-center justify-center min-w-[90px]"
          >
            {exchangeLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
            ) : (
              "Confirm"
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Loading spinner
  if (loading) {
    return <OrderDetailsSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 p-6 rounded-lg shadow-md w-full mx-auto mt-10">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-red-100 rounded-full p-3">
            <FiAlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-red-800">
              Unable to load order details
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={() => navigate(-1)}
              >
                <FiArrowLeft className="mr-2" />
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No order found
  if (!order) {
    return (
      <div className="text-center py-16 bg-white rounded-lg shadow-md w-full mx-auto mt-10">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
          <BsBox className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="mt-6 text-lg font-medium text-gray-900">
          Order not found
        </h3>
        <p className="mt-2 text-base text-gray-500 max-w-sm mx-auto">
          We couldn't find the order you're looking for.
        </p>
        <div className="mt-6">
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            onClick={() => navigate("/orders")}
          >
            <FiArrowLeft className="mr-2" />
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4 w-full">
        {/* Back button */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center text-gray-600 hover:text-orange-600 transition-colors"
        >
          <FiArrowLeft className="mr-2" />
          <span>Back to Orders</span>
        </button>

        {/* Order Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 flex items-center mb-1">
                <IoReceiptOutline className="mr-3 text-orange-500" />
                Order #{order._id.slice(-8).toUpperCase()}
              </h1>
              <p className="text-gray-600 flex items-center">
                <FiCalendar className="mr-2" />
                Placed on {formatDate(order.createdAt)}
              </p>
            </div>
            <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
              <StatusBadge status={order.status} />
              <ExchangeBadge exchangeRequest={order.exchangeRequest} />
            </div>
          </div>

          {/* Exchange Details */}
          {order.exchangeRequest && order.exchangeRequest.isRequested && (
            <div className="mt-4 bg-gray-50 p-4 rounded-md">
              <h3 className="font-medium text-gray-700 flex items-center">
                <MdOutlineLoop className="mr-2 text-orange-500" />
                Exchange Information
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2 text-sm">
                <div>
                  <span className="text-gray-500">Requested:</span>{" "}
                  <span>{formatDate(order.exchangeRequest.requestedAt)}</span>
                </div>
                {order.exchangeRequest.completedAt && (
                  <div>
                    <span className="text-gray-500">Completed:</span>{" "}
                    <span>{formatDate(order.exchangeRequest.completedAt)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Product List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <h2 className="text-lg font-medium text-gray-800 flex items-center">
                  <FiPackage className="mr-2 text-orange-500" />
                  Order Items ({order.products.length})
                </h2>
              </div>
              <div className="divide-y divide-gray-200">
                {order.products.map((item) => (
                  <div
                    key={item._id}
                    className="p-6 flex flex-col sm:flex-row hover:bg-gray-50"
                  >
                    <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 mb-4 sm:mb-0">
                      <img
                        src={item?.product?.images[0]}
                        alt={item?.product?.name}
                        className="h-full w-full object-cover object-center"
                      />
                    </div>
                    <div className="sm:ml-6 flex flex-1 flex-col">
                      <div className="flex justify-between">
                        <div>
                          <h3 className="text-base font-medium text-gray-900">
                            {item?.product?.name}
                          </h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Brand: {item?.product?.brand}
                          </p>
                          <p className="mt-1 text-sm text-gray-500">
                            Product ID: {item?.product?.bb_id}
                          </p>
                          <div className="mt-2">
                            <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded mr-2">
                              Size: {item.size}
                            </span>
                            <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded">
                              Qty: {item.quantity}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-medium text-gray-900">
                            ₹{(item.price * item.quantity).toLocaleString()}
                          </p>
                          <p className="text-sm text-gray-500">
                            ₹{item.price.toLocaleString()} each
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Exchange Button - Show only for delivered orders without exchange requests */}
            {order.status === "Delivered" &&
              (!order.exchangeRequest ||
                !order.exchangeRequest.isRequested) && (
                <div className="mt-6">
                  <button
                    onClick={handleExchangeRequest}
                    disabled={exchangeLoading}
                    className="w-full sm:w-auto flex items-center justify-center px-6 py-3 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-md shadow transition-colors"
                  >
                    <FiRefreshCw className="mr-2" />
                    {exchangeLoading ? "Processing..." : "Request Exchange"}
                  </button>
                </div>
              )}
          </div>

          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <h2 className="text-lg font-medium text-gray-800 flex items-center">
                  <FiMapPin className="mr-2 text-orange-500" />
                  Shipping Address
                </h2>
              </div>
              <div className="p-3">
                {order.shippingAddress ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center capitalize">
                      <p className="flex items-center">
                        <FiUser className="mr-2 text-gray-400" />
                        <span>{order.shippingAddress.fullName}</span>
                      </p>
                      <div className="p-1 rounded-lg bg-orange-700 text-white text-sm">
                        <p>{order.shippingAddress.addressType} </p>
                      </div>
                    </div>
                    <p className="flex items-center">
                      <FiPhone className="mr-2 text-gray-400" />
                      <span>{order.shippingAddress.phone}</span>
                    </p>
                    <p className="flex items-start">
                      <FiHome className="mr-2 mt-1 text-gray-400" />
                      <span>
                        {order.shippingAddress.addressLine1},{" "}
                        {order.shippingAddress.addressLine2},{" "}
                        {order.shippingAddress.city},{" "}
                        {order.shippingAddress.state} ,{" "}
                        {order.shippingAddress.country} ,{" "}
                        {order.shippingAddress.postalCode}
                      </span>
                    </p>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">
                    No shipping address provided
                  </p>
                )}
              </div>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                <h2 className="text-lg font-medium text-gray-800 flex items-center">
                  <BsCreditCard2Back className="mr-2 text-orange-500" />
                  Order Summary
                </h2>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">
                      ₹{subTotal.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST Tax</span>
                    <span className="font-medium">
                      ₹{gstTax.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Extra Tax</span>
                    <span className="font-medium">
                      ₹{extraTax.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Delivery Fee</span>
                    <span className="font-medium">
                      ₹{shippingCharge.toLocaleString()}
                    </span>
                  </div>

                  <div className="border-t border-gray-200 pt-4 flex justify-between">
                    <span className="font-bold text-gray-800">Total</span>
                    <span className="font-bold text-xl text-gray-900">
                      ₹{totalPrice.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {order.pod_img_url && (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
                  <h2 className="text-lg font-medium text-gray-800 flex items-center">
                    <BsTruck className="mr-2 text-orange-500" />
                    Proof of Delivery
                  </h2>
                </div>
                <div className="p-6">
                  <a
                    href={order.pod_img_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img
                      src={order.pod_img_url}
                      alt="Delivery Proof"
                      className="w-full h-auto rounded-md shadow-sm"
                    />
                    <p className="mt-2 text-center text-sm text-blue-600 hover:text-blue-800">
                      View Full Image
                    </p>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Exchange Modal */}
      <ExchangeModal />
    </div>
  );
};

export default OrderDetails;
